// ── DASH.B1 — Zone 3: Activity Feed ─────────────────────────────────────────
//
// Right column (~40%), last-24h timeline (design doc §5). Each item is 64px
// tall (icon / summary / timestamp). The activity data itself comes from
// DASH.A2's GET /dashboard/activity — a SEPARATE endpoint that is NOT a hard
// dependency of B1: until it lands, this zone shows an empty timeline plus the
// "View all activity →" link. The /overview payload advertises the endpoint via
// `activity_feed_endpoint` so the path isn't hardcoded.

import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import type { DashboardOverview } from '../../types/dashboard';
import { formatTimeAgo } from '../../lib/dashboardFormat';

export interface ActivityItem {
  id: string;
  eventType: string;
  summary: string;
  timestamp: string;
  contextUrl: string;
}

// The frontend activity-feed PAGE route does not exist yet: DASH.A2 (#822)
// shipped the activity ENDPOINT, but a dedicated activity page is a later
// story. A live "View all activity →" link to a non-existent route would 404 —
// worse than no link — so it is gated off until the route lands. When the
// activity page ships, set this to its path (e.g. '/activity') to light the
// link back up; nothing else here changes.
// TODO(#822 follow-up — activity page route): set ACTIVITY_PAGE_PATH.
const ACTIVITY_PAGE_PATH: string | null = null;

const ITEM_HEIGHT = 64; // §11 sizing

function ActivityFeedItem({ item }: { item: ActivityItem }) {
  return (
    <Link
      to={item.contextUrl}
      data-testid="activity-feed-item"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        height: ITEM_HEIGHT,
        padding: '0 4px',
        borderTop: '1px solid var(--border)',
        color: 'var(--text)',
        textDecoration: 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'var(--primary)',
          flexShrink: 0,
        }}
      />
      <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.summary}
      </span>
      <span style={{ fontSize: 11, color: 'var(--text-secondary)', flexShrink: 0 }}>
        {formatTimeAgo(item.timestamp)}
      </span>
    </Link>
  );
}

export interface DashboardZone3ActivityProps {
  overview: DashboardOverview | null;
  loading: boolean;
  density: 'compact' | 'standard';
  /** Injectable for tests; defaults to the (not-yet-built) frontend route. */
  items?: ActivityItem[];
  // #1600: the activity feed is a SEPARATE fetch from `overview` (different
  // round-trip, can resolve after `loading` has already gone false). Without
  // this, the zone briefly renders zone3-empty between overview loading and
  // the activity fetch actually resolving. Defaults to false so every
  // pre-#1600 caller/test (loading tied only to overview) is unaffected.
  itemsLoading?: boolean;
}

export default function DashboardZone3Activity({ overview, loading, density, items = [], itemsLoading = false }: DashboardZone3ActivityProps) {
  const maxItems = density === 'compact' ? 5 : 10;
  const visible = items.slice(0, maxItems);
  const showSkeleton = (loading && !overview) || itemsLoading;

  const containerStyle: CSSProperties = {
    padding: 20,
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border)',
    background: 'var(--surface)',
    boxShadow: 'var(--shadow-sm)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  return (
    <div data-testid="dashboard-zone3" style={containerStyle}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, fontFamily: 'var(--font-display)', margin: 0 }}>Recent activity</h2>
      </header>

      {showSkeleton ? (
        <div data-testid="zone3-skeleton" className="animate-pulse" aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ height: ITEM_HEIGHT - 16, background: 'var(--surface-alt)', borderRadius: 4 }} />
          ))}
        </div>
      ) : visible.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {visible.map((item) => (
            <ActivityFeedItem key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div data-testid="zone3-empty" style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
          No recent activity in the last 24 hours.
        </div>
      )}

      {/* "View all" is rendered ONLY when the activity page route exists (see
          ACTIVITY_PAGE_PATH) — never a live link to a 404. The backend endpoint
          path itself is advertised at overview.activity_feed_endpoint. */}
      {ACTIVITY_PAGE_PATH && (
        <Link
          to={ACTIVITY_PAGE_PATH}
          data-testid="activity-view-all"
          style={{ color: 'var(--primary)', fontSize: 13, fontWeight: 500, textDecoration: 'none', marginTop: 4 }}
        >
          View all activity →
        </Link>
      )}
    </div>
  );
}
